import dayjs from 'dayjs';
import { distanceMeters, LatLon } from './demo-trajectory';

/**
 * ADR-045 — lecture d'un fichier de trajectoire de démo (Outils › Trajectoires de démo › Importer).
 * CSV : latitude, longitude, puis en option altitude et horodatage (ISO 8601 ou epoch) ; en-tête
 * facultatif, séparateur virgule, point-virgule ou tabulation. GPX : les points `<trkpt>`.
 */

export interface ImportedPoint extends LatLon {
  altitude?: number;
  /** Epoch ms. */
  time?: number;
}

export interface ImportedTrack {
  kind: 'CSV' | 'GPX';
  /** Nom du fichier sans extension : nom proposé pour la trajectoire. */
  name: string;
  points: ImportedPoint[];
  /** Points horodatés. */
  timed: number;
  /** Lignes illisibles ou hors des bornes WGS84, ignorées. */
  skipped: number;
}

/** Contrat `DemoTrajectoryRequest.points` : 2 à 5 000 points. */
export const DEMO_MAX_POINTS = 5000;
/** Contrat `speedMps` : 0,2 à 50 m/s. */
const SPEED_MIN = 0.2;
const SPEED_MAX = 50;

const validLatLon = (lat: number, lon: number) =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

function parseTime(raw: string | null | undefined): number | undefined {
  const s = (raw ?? '').trim();
  if (!s) return undefined;
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    // Epoch en secondes ou en millisecondes.
    return n > 1e11 ? n : n * 1000;
  }
  const d = dayjs(s);
  return d.isValid() ? d.valueOf() : undefined;
}

function point(lat: number, lon: number, alt: number | undefined, time: number | undefined): ImportedPoint {
  const p: ImportedPoint = { latitude: lat, longitude: lon };
  if (alt !== undefined && Number.isFinite(alt)) p.altitude = alt;
  if (time !== undefined) p.time = time;
  return p;
}

function parseGpx(text: string): Pick<ImportedTrack, 'points' | 'skipped'> {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const points: ImportedPoint[] = [];
  let skipped = 0;
  for (const el of Array.from(doc.getElementsByTagName('trkpt'))) {
    const lat = Number(el.getAttribute('lat'));
    const lon = Number(el.getAttribute('lon'));
    if (!validLatLon(lat, lon)) {
      skipped++;
      continue;
    }
    const ele = el.getElementsByTagName('ele')[0]?.textContent;
    const time = el.getElementsByTagName('time')[0]?.textContent;
    points.push(point(lat, lon, ele ? Number(ele) : undefined, parseTime(time)));
  }
  return { points, skipped };
}

const COLUMNS: Record<'lat' | 'lon' | 'alt' | 'time', RegExp> = {
  lat: /^lat(itude)?$/,
  lon: /^(lon|lng|long|longitude)$/,
  alt: /^(alt|altitude|ele|elevation|hauteur)$/,
  time: /^(time|timestamp|horodatage|date|datetime|heure)$/,
};

function parseCsv(text: string): Pick<ImportedTrack, 'points' | 'skipped'> {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length === 0) return { points: [], skipped: 0 };
  const sepOf = (l: string) => (l.includes('\t') ? '\t' : l.includes(';') ? ';' : ',');
  // Point-virgule ou tabulation : la virgule peut être le séparateur décimal (export Excel français).
  const num = (s: string | undefined, sep: string) =>
    s === undefined || s.trim() === '' ? NaN : Number(sep === ',' ? s.trim() : s.trim().replace(',', '.'));

  let idx = { lat: 0, lon: 1, alt: 2, time: 3 };
  let body = lines;
  const head = lines[0].split(sepOf(lines[0])).map(c => c.trim().toLowerCase().replace(/^"|"$/g, ''));
  if (head.some(c => /[a-z]/.test(c) && !/^[-+\d.,e]+$/.test(c))) {
    body = lines.slice(1);
    const find = (re: RegExp) => head.findIndex(c => re.test(c));
    const found = { lat: find(COLUMNS.lat), lon: find(COLUMNS.lon), alt: find(COLUMNS.alt), time: find(COLUMNS.time) };
    if (found.lat >= 0 && found.lon >= 0) idx = found;
  }

  const points: ImportedPoint[] = [];
  let skipped = 0;
  for (const line of body) {
    const sep = sepOf(line);
    const c = line.split(sep).map(s => s.trim().replace(/^"|"$/g, ''));
    const lat = num(c[idx.lat], sep);
    const lon = num(c[idx.lon], sep);
    if (!validLatLon(lat, lon)) {
      skipped++;
      continue;
    }
    const alt = idx.alt >= 0 ? num(c[idx.alt], sep) : NaN;
    points.push(
      point(lat, lon, Number.isFinite(alt) ? alt : undefined, idx.time >= 0 ? parseTime(c[idx.time]) : undefined)
    );
  }
  return { points, skipped };
}

/** Lit un fichier CSV ou GPX (reconnu à son contenu, sinon à son extension). */
export function parseTrackFile(text: string, fileName: string): ImportedTrack {
  const isGpx = /<gpx[\s>]/i.test(text) || /\.gpx$/i.test(fileName);
  const { points, skipped } = isGpx ? parseGpx(text) : parseCsv(text);
  return {
    kind: isGpx ? 'GPX' : 'CSV',
    name: fileName.replace(/\.(csv|gpx|txt)$/i, ''),
    points,
    timed: points.filter(p => p.time !== undefined).length,
    skipped,
  };
}

/**
 * Vitesse moyenne des horodatages : distance parcourue entre le premier et le dernier point
 * horodaté, divisée par le temps écoulé ; au dixième, bornée à 0,2..50 m/s. `null` si le fichier
 * n'a pas deux horodatages distincts.
 */
export function averageSpeedMps(points: readonly ImportedPoint[]): number | null {
  const first = points.findIndex(p => p.time !== undefined);
  let last = -1;
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i].time !== undefined) {
      last = i;
      break;
    }
  }
  if (first < 0 || last <= first) return null;
  const seconds = (points[last].time! - points[first].time!) / 1000;
  if (!(seconds > 0)) return null;
  let meters = 0;
  for (let i = first + 1; i <= last; i++) meters += distanceMeters(points[i - 1], points[i]);
  const v = Math.round((meters / seconds) * 10) / 10;
  return Math.min(SPEED_MAX, Math.max(SPEED_MIN, v));
}

/** Au plus `max` points : un sur k, en gardant le premier et le dernier. */
export function thinPoints<P>(points: readonly P[], max: number = DEMO_MAX_POINTS): readonly P[] {
  if (points.length <= max) return points;
  const step = Math.ceil((points.length - 1) / (max - 1));
  const out: P[] = [];
  for (let i = 0; i < points.length - 1; i += step) out.push(points[i]);
  out.push(points[points.length - 1]);
  return out;
}
